import { normalizeRevisionAdviceText } from '../listing-draft-payload.js';

function revisionPayloadOffendingTerms(primary = {}, fallback = {}) {
  const candidates = [
    primary.offendingTerms,
    primary.evidence?.offendingTerms,
    fallback.offendingTerms,
    fallback.evidence?.offendingTerms,
  ];
  const terms = candidates.find((value) => Array.isArray(value) && value.length) || [];
  return [...new Set(terms.map((term) => String(term || '').trim()).filter(Boolean))];
}

function compactLedgerTerms(values, limit = 80) {
  const seen = new Set();
  const output = [];
  for (const value of Array.isArray(values) ? values : []) {
    const term = String(
      value && typeof value === 'object'
        ? value.term || value.keyword || value.phrase || value.value || ''
        : value || ''
    ).replace(/\s+/gu, ' ').trim();
    const key = term.toLocaleLowerCase();
    if (!term || seen.has(key)) continue;
    seen.add(key);
    output.push(term.slice(0, 240));
    if (output.length >= limit) break;
  }
  return output;
}

/**
 * Step04 needs only the immutable Search Team selection and the same-run
 * replacement pool. Full prompts, raw LLM output, and prior trace calls are
 * useful for inspection but must never be sent back with each revision.
 */
export function compactRevisionGenerationLedger(generationLedger = null) {
  if (!generationLedger || typeof generationLedger !== 'object') return null;
  const searchStage = (Array.isArray(generationLedger.stages) ? generationLedger.stages : [])
    .find((stage) => stage?.stage === 'search_team');
  const accepted = searchStage?.acceptedOutput && typeof searchStage.acceptedOutput === 'object'
    ? searchStage.acceptedOutput
    : {};
  const selected = compactLedgerTerms(
    accepted.selected || accepted.candidates || generationLedger?.searchTerms?.selected,
    80,
  );
  const keywordPool = compactLedgerTerms(
    generationLedger.step03KeywordPool
      || generationLedger?.step02?.keywordPool
      || accepted.keywordPool,
    80,
  );
  if (!selected.length && !keywordPool.length) return null;
  return {
    schemaVersion: 'listing_step04_search_terms_ledger.v1',
    ...(keywordPool.length ? { step03KeywordPool: keywordPool } : {}),
    stages: selected.length ? [{
      stage: 'search_team',
      acceptedOutput: {
        selected,
        candidates: selected,
        ...(keywordPool.length ? { keywordPool } : {}),
      },
    }] : [],
  };
}

/**
 * Convert presentation-only suggestion groups into the legacy compatibility
 * payload. The server's authoritative revision instruction remains prose.
 */
export function buildRevisionPayload(selected) {
  return (selected || []).flatMap((suggestion) => {
    if (!suggestion) return [];
    const editedTrim = String(suggestion.editedSuggestion || '').trim();
    const originalGroupAction = String(suggestion.suggestion || suggestion.recommendedAction || '').trim();
    const groupWasEdited = Boolean(editedTrim && editedTrim !== originalGroupAction);
    if (Array.isArray(suggestion.items) && suggestion.items.length > 0) {
      return suggestion.items.map((item, index) => {
        const offendingTerms = revisionPayloadOffendingTerms(item, suggestion);
        return {
          id: item.id || `${suggestion.id}-${index}`,
          field: item.field || suggestion.field,
          dimension: item.dimension || suggestion.dimension || '',
          severity: item.severity || suggestion.severity,
          ruleCode: item.ruleCode || item.type || suggestion.ruleCode || '',
          level: item.level || suggestion.level || '',
          businessImpact: item.businessImpact || suggestion.businessImpact || '',
          evidenceRequired: item.evidenceRequired === true || suggestion.evidenceRequired === true,
          blocksWriteback: item.blocksWriteback === true || suggestion.blocksWriteback === true,
          revisionAction: item.revisionAction || suggestion.revisionAction || '',
          issue: item.issue || suggestion.issue,
          suggestion: (groupWasEdited
            ? editedTrim
            : (item.editedSuggestion || item.suggestion || item.recommendedAction || originalGroupAction)).trim(),
          original: item.original || suggestion.original || '',
          evidence: item.evidence || suggestion.evidence || null,
          ...(offendingTerms.length ? { offendingTerms } : {}),
        };
      });
    }
    const offendingTerms = revisionPayloadOffendingTerms(suggestion);
    return [{
      id: suggestion.id,
      field: suggestion.field,
      dimension: suggestion.dimension || '',
      severity: suggestion.severity,
      ruleCode: suggestion.ruleCode || '',
      level: suggestion.level || '',
      businessImpact: suggestion.businessImpact || '',
      evidenceRequired: suggestion.evidenceRequired === true,
      blocksWriteback: suggestion.blocksWriteback === true,
      revisionAction: suggestion.revisionAction || '',
      issue: suggestion.issue,
      suggestion: editedTrim || originalGroupAction,
      original: suggestion.original,
      evidence: suggestion.evidence || null,
      ...(offendingTerms.length ? { offendingTerms } : {}),
    }];
  });
}

const MASTER_REVISION_FIELD_LABELS = {
  general: '整体',
  title: '标题',
  bullets: '五点',
  description: '商品描述',
  aplus: 'A+ 模块',
  qa: 'Q&A',
  searchTerms: 'Search Terms',
};

export function buildMasterRevisionAdviceText(masterInstructions = {}) {
  return Object.entries(MASTER_REVISION_FIELD_LABELS)
    .map(([field, label]) => {
      const instruction = normalizeRevisionAdviceText(masterInstructions?.[field]);
      return instruction ? `${label}：${instruction}` : '';
    })
    .filter(Boolean)
    .join('\n\n');
}

/**
 * Build the only authoritative Step04 execution instruction. Suggestion cards
 * remain an explanation layer and cannot become client-side execution text.
 */
export function buildEffectiveRevisionAdviceText({
  revisionAdviceText = '',
  masterInstructions = {},
  searchTermsChanged = false,
} = {}) {
  const parts = [];
  const diagnosedAdvice = normalizeRevisionAdviceText(revisionAdviceText);
  if (diagnosedAdvice) parts.push(diagnosedAdvice);
  const masterAdvice = buildMasterRevisionAdviceText(masterInstructions);
  if (masterAdvice) parts.push(`用户补充要求：\n${masterAdvice}`);
  if (searchTermsChanged) {
    parts.push('保留用户已在界面中调整后的 Search Terms；只在发现明确的合规、侵权或商品事实风险时再做必要修正。');
  }
  return parts.join('\n\n').trim();
}
